/**
 * Exit-intent newsletter popup.
 *
 * Triggers:
 *   - Desktop: cursor leaves viewport via the top edge (toward the address bar).
 *   - Mobile/touch: a random delay between 45–60s OR scroll past 70% of the page,
 *                   whichever fires first.
 *
 * Suppression — popup never fires if any of these are true:
 *   - The user has focused or clicked any form input/control on the page.
 *   - A donate-themed CTA has been clicked (button text matching donate/give/pay).
 *   - Focus has been captured by an iframe (Stripe Elements, GoCardless, etc.).
 *   - The document is hidden, or another modal is currently open.
 *   - localStorage flags say the user has dismissed it within the last 30 days,
 *     or has already submitted.
 *   - The popup has already been shown once this page view.
 *
 * Submission is delegated to the existing Donorfy iframe form (same one used by
 * flexible-content/sections/newsletter-section.php). We can't observe completion
 * cross-origin, so clicking "Sign up" sets the 30-day cooldown.
 */

(function () {
	'use strict';

	if (typeof window === 'undefined' || typeof document === 'undefined') return;

	var STORAGE_KEYS = {
		dismissed: 'stmartins_exitPopupDismissedAt',
		submitted: 'stmartins_exitPopupSubmitted'
	};

	var COOLDOWN_DISMISS_DAYS = 30;
	var DESKTOP_GRACE_MS      = 3000;
	var MOBILE_TIMER_MIN_MS   = 45000;
	var MOBILE_TIMER_MAX_MS   = 60000;
	var SCROLL_THRESHOLD      = 0.7;
	var IFRAME_FOCUS_POLL_MS  = 500;
	var FORM_INTERACT_DEBOUNCE_MS = 10000;

	var DONATE_TEXT_RE = /(donate|give|pay|sponsor|fundraise|gift\s*aid|£|\$)/i;
	var SIGNUP_TEXT_RE = /(sign\s*up|subscribe|newsletter|join)/i;

	var popup, popupCloseBtn, popupSignupBtn;
	var lightbox, lightboxCloseBtn, lightboxIframe;

	var hasShown = false;
	var userInteractedWithForm = false;
	var lastFormInteractionAt = 0;
	var hasFiredMouseListener = false;

	function ready(fn) {
		if (document.readyState !== 'loading') fn();
		else document.addEventListener('DOMContentLoaded', fn);
	}

	function getNumber(key) {
		try {
			var v = window.localStorage.getItem(key);
			return v ? parseInt(v, 10) : 0;
		} catch (e) { return 0; }
	}

	function setNumber(key, value) {
		try { window.localStorage.setItem(key, String(value)); } catch (e) {}
	}

	function setFlag(key) {
		try { window.localStorage.setItem(key, '1'); } catch (e) {}
	}

	function getFlag(key) {
		try { return window.localStorage.getItem(key) === '1'; } catch (e) { return false; }
	}

	function daysSince(ts) {
		if (!ts) return Infinity;
		return (Date.now() - ts) / (1000 * 60 * 60 * 24);
	}

	function pushEvent(name, data) {
		try {
			window.dataLayer = window.dataLayer || [];
			var payload = { event: name };
			if (data) {
				for (var k in data) if (Object.prototype.hasOwnProperty.call(data, k)) payload[k] = data[k];
			}
			window.dataLayer.push(payload);
		} catch (e) {}
	}

	function isTouchDevice() {
		try {
			return window.matchMedia('(pointer: coarse)').matches;
		} catch (e) {
			return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
		}
	}

	function anyOtherModalOpen() {
		// Existing newsletter-section iframe lightbox, plus generic markers.
		var existing = document.querySelector('.lightbox.active:not(#exit-popup-lightbox)');
		if (existing) return true;
		if (document.body && document.body.classList.contains('modal-open')) return true;
		return false;
	}

	function anyFormHasInput() {
		var fields = document.querySelectorAll('form input, form textarea, form select');
		for (var i = 0; i < fields.length; i++) {
			var f = fields[i];
			if (f.type === 'hidden' || f.type === 'submit' || f.type === 'button') continue;
			if (f.type === 'checkbox' || f.type === 'radio') {
				if (f.checked) return true;
				continue;
			}
			if (f.value && String(f.value).trim().length > 0) return true;
		}
		return false;
	}

	function focusIsInIframe() {
		var el = document.activeElement;
		return !!(el && el.tagName === 'IFRAME');
	}

	function shouldSuppress() {
		if (hasShown) return true;
		if (userInteractedWithForm) return true;
		if (document.visibilityState && document.visibilityState !== 'visible') return true;
		if (anyOtherModalOpen()) return true;
		if (focusIsInIframe()) return true;
		if (anyFormHasInput()) return true;

		if (Date.now() - lastFormInteractionAt < FORM_INTERACT_DEBOUNCE_MS) return true;

		if (getFlag(STORAGE_KEYS.submitted)) return true;
		if (daysSince(getNumber(STORAGE_KEYS.dismissed)) < COOLDOWN_DISMISS_DAYS) return true;

		return false;
	}

	function markFormInteraction() {
		userInteractedWithForm = true;
		lastFormInteractionAt = Date.now();
	}

	function markRecentInteraction() {
		lastFormInteractionAt = Date.now();
	}

	function attachInteractionListeners() {
		document.addEventListener('focusin', function (e) {
			if (!e.target) return;
			var tag = (e.target.tagName || '').toLowerCase();
			if (tag === 'input' || tag === 'textarea' || tag === 'select') {
				markFormInteraction();
			}
		}, true);

		document.addEventListener('click', function (e) {
			if (!e.target) return;
			var t = e.target;

			if (t.closest && t.closest('form')) {
				markFormInteraction();
				return;
			}

			var clickable = t.closest ? t.closest('a, button, [role=\'button\']') : null;
			if (clickable) {
				var label = (clickable.textContent || clickable.getAttribute('aria-label') || '').trim();
				if (DONATE_TEXT_RE.test(label) || SIGNUP_TEXT_RE.test(label)) {
					markFormInteraction();
					return;
				}
			}

			markRecentInteraction();
		}, true);

		// Cross-origin iframes don't bubble events; poll for focus capture.
		setInterval(function () {
			if (focusIsInIframe()) markFormInteraction();
		}, IFRAME_FOCUS_POLL_MS);

		document.addEventListener('change', function (e) {
			if (!e.target) return;
			if (e.target.closest && e.target.closest('form')) markFormInteraction();
		}, true);
	}

	function show() {
		if (!popup || hasShown) return;
		if (shouldSuppress()) return;

		hasShown = true;
		popup.classList.add('is-open');
		popup.setAttribute('aria-hidden', 'false');
		document.body.style.overflow = 'hidden';
		pushEvent('exit_popup_shown');
	}

	function hide(reason) {
		if (!popup) return;
		popup.classList.remove('is-open');
		popup.setAttribute('aria-hidden', 'true');
		document.body.style.overflow = '';
		setNumber(STORAGE_KEYS.dismissed, Date.now());
		pushEvent('exit_popup_dismissed', { reason: reason || 'close' });
	}

	function openLightbox() {
		if (!lightbox || !lightboxIframe) return;

		// Lazy-load the iframe src on first open to keep initial pageload fast.
		var src = lightboxIframe.getAttribute('src');
		if (!src) {
			var dataSrc = lightboxIframe.getAttribute('data-src');
			if (dataSrc) lightboxIframe.setAttribute('src', dataSrc);
		}

		lightbox.classList.add('active');
		lightbox.setAttribute('aria-hidden', 'false');
		document.body.style.overflow = 'hidden';
	}

	function closeLightbox() {
		if (!lightbox) return;
		lightbox.classList.remove('active');
		lightbox.setAttribute('aria-hidden', 'true');
		document.body.style.overflow = '';
	}

	function onSignupClick() {
		// Treat clicking Sign up as engagement — apply 30-day cooldown.
		setNumber(STORAGE_KEYS.dismissed, Date.now());
		pushEvent('exit_popup_signup_clicked');

		// Hand off to the Donorfy iframe lightbox.
		popup.classList.remove('is-open');
		popup.setAttribute('aria-hidden', 'true');
		openLightbox();
	}

	function attachDesktopExitIntent() {
		if (hasFiredMouseListener) return;
		hasFiredMouseListener = true;

		setTimeout(function () {
			document.documentElement.addEventListener('mouseleave', function (e) {
				if (e.clientY > 0) return;
				show();
			});
		}, DESKTOP_GRACE_MS);
	}

	function attachMobileTriggers() {
		var fired = false;
		var fire = function () {
			if (fired) return;
			fired = true;
			show();
		};

		var delay = MOBILE_TIMER_MIN_MS +
			Math.floor(Math.random() * (MOBILE_TIMER_MAX_MS - MOBILE_TIMER_MIN_MS));
		setTimeout(fire, delay);

		var onScroll = function () {
			var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
			var docHeight = Math.max(
				document.documentElement.scrollHeight,
				document.body ? document.body.scrollHeight : 0
			);
			var winHeight = window.innerHeight || document.documentElement.clientHeight;
			var scrollable = docHeight - winHeight;
			if (scrollable <= 0) return;
			if (scrollTop / scrollable >= SCROLL_THRESHOLD) {
				window.removeEventListener('scroll', onScroll);
				fire();
			}
		};
		window.addEventListener('scroll', onScroll, { passive: true });
	}

	function bindCloseHandlers() {
		if (popupCloseBtn) {
			popupCloseBtn.addEventListener('click', function () { hide('close-button'); });
		}
		if (popup) {
			popup.addEventListener('click', function (e) {
				if (e.target === popup) hide('backdrop');
			});
		}
		if (popupSignupBtn) {
			popupSignupBtn.addEventListener('click', onSignupClick);
		}
		if (lightboxCloseBtn) {
			lightboxCloseBtn.addEventListener('click', closeLightbox);
		}
		if (lightbox) {
			lightbox.addEventListener('click', function (e) {
				if (e.target === lightbox) closeLightbox();
			});
		}
		document.addEventListener('keydown', function (e) {
			if (e.key !== 'Escape') return;
			if (lightbox && lightbox.classList.contains('active')) {
				closeLightbox();
			} else if (popup && popup.classList.contains('is-open')) {
				hide('escape');
			}
		});
	}

	ready(function () {
		popup            = document.getElementById('exit-popup');
		if (!popup) return;
		popupCloseBtn    = document.getElementById('exit-popup-close');
		popupSignupBtn   = document.getElementById('exit-popup-signup');
		lightbox         = document.getElementById('exit-popup-lightbox');
		lightboxCloseBtn = document.getElementById('exit-popup-lightbox-close');
		lightboxIframe   = lightbox ? lightbox.querySelector('[data-exit-popup-iframe]') : null;

		// Hard-stop: don't initialise if cooldowns already prevent display.
		if (getFlag(STORAGE_KEYS.submitted)) return;
		if (daysSince(getNumber(STORAGE_KEYS.dismissed)) < COOLDOWN_DISMISS_DAYS) return;

		bindCloseHandlers();
		attachInteractionListeners();

		if (isTouchDevice()) {
			attachMobileTriggers();
		} else {
			attachDesktopExitIntent();
		}
	});
}());
